<template>
  <div class="package-table">
    <div class="row q-gutter-md q-mb-md items-end">
      <div class="col-md-3 col-sm-6 col-xs-12">
        <CustomSelect
          :options="placementTypeOptions"
          label="Тип помещения"
          optionValueKey="placement_type_id"
          optionLabelKey="placement_type_name"
          :value="selectedPlacementType || ''"
          clearable
          @change="(value, row) => onChange('placement_type', value, row)"
        />
      </div>
      <div class="col-md-3 col-sm-6 col-xs-12">
        <CustomSelect
          :options="workSetOptions"
          label="Конструктив"
          optionValueKey="work_set_id"
          optionLabelKey="work_set_name"
          :value="selectedWorkSet || ''"
          clearable
          @change="(value, row) => onChange('work_set', value, row)"
        />
      </div>
      <div class="col d-flex justify-end" style="text-align: end;">
        <CustomButton class="createBtn" @click="onAddToPackageClick">
          <div class="tw-flex tw-gap-2 tw-items-center">
            <span>Занести в пакет</span>
            <q-icon name="img:img/icons/add_box.svg" size="16px" />
          </div>
        </CustomButton>
      </div>
    </div>

    <CustomTable
      :title="tableTitle"
      :rowsData="tableData"
      :columns="columns"
      :loading="isLoading"
      :custom-body="true"
      :small="true"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.placement_type_name }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.work_set_name }}
          </custom-table-cell>
          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.set_name }}
          </custom-table-cell>
          <custom-table-cell align="left">
            <div class="tw-flex tw-justify-end">
              <q-chip
                v-if="props.row.is_default"
                size="xs"
                outline
                square
                style="font-size: 10px; font-weight: 500; margin-right: 0; margin-top: 0;"
                class="tw-text-primary-light"
                label="По умолчанию"
              >
                <q-tooltip>
                  Материалы включенные пол умолчанию в пакет
                </q-tooltip>
              </q-chip>
            </div>
            <div class="tw-flex tw-flex-col tw-gap-2">
              <div
                v-for="(item, index) in props.row.material_name"
                :key="index"
              >
                <span style="font-weight: 500;">{{ index + 1 }}.</span>
                {{ item }}
              </div>
            </div>
          </custom-table-cell>
          <custom-table-cell align="center" vertical-align="center">
            <div style="display: flex; flex-direction: column; gap: 24px;">
              <CustomIcon
                icon="edit"
                title="Редактировать"
                @click="handleEditClick(props.row)"
              />
              <CustomIcon
                icon="trash"
                :approve-mode="true"
                :approve="() => deleteItem(props.row)"
                :loading="deleting[getDeleteKey(props.row)]"
              />
            </div>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomTable from '@/components/common/CustomTable'
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell'
import CustomSelect from '@/components/form/CustomSelect'
import CustomIcon from '@/components/common/CustomIcon'
import {
  getRoughFinishingData,
  getPlacementTypes,
  getWorkSets,
  packageTableColumns,
  deletePackageEntry,
  updPackageEntry
} from './services'
import { MODAL_NAMES } from '@/constants'
import { useAppStore } from '@/services/store/app'
import CustomButton from "@/components/common/CustomButton.vue";

const props = defineProps({
  presetKitId: {
    type: Number,
    required: true
  },
  title: {
    type: String,
    default: 'Таблица пакета'
  }
})
const { showSucceedSnackbar } = useCustomSnackbar();

const appStore = useAppStore()
const { showModal, closeModal } = appStore

const selectedPlacementType = ref(null)
const selectedWorkSet = ref(null)
const isLoading = ref(false)
const deleting = ref({})

const columns = packageTableColumns
const tableTitle = computed(() => props.title)

const { data: placementTypesData } = useQuery({
  queryKey: ['placementTypes', props.presetKitId],
  queryFn: () => getPlacementTypes(props.presetKitId),
  refetchOnWindowFocus: false,
  initialData: { data: [] },
  enabled: computed(() => !!props.presetKitId)
})

const { data: workSetsData, refetch } = useQuery({
  queryKey: ['workSets', props.presetKitId],
  queryFn: () => getWorkSets(props.presetKitId),
  refetchOnWindowFocus: false,
  initialData: { data: [] },
  enabled: computed(() => !!props.presetKitId)
})

const { data: tableDataResponse, refetch: refetchTableData } = useQuery({
  queryKey: ['packageTableData', props.presetKitId, selectedPlacementType, selectedWorkSet],
  queryFn: () => getRoughFinishingData(props.presetKitId, selectedPlacementType.value, selectedWorkSet.value),
  refetchOnWindowFocus: false,
  initialData: { data: [] },
  enabled: computed(() => !!props.presetKitId)
})

const placementTypeOptions = computed(() => placementTypesData.value?.data || [])
const workSetOptions = computed(() => workSetsData.value?.data || [])
const tableData = computed(() => tableDataResponse.value?.data || [])

function onChange(key, value, row) {
  if (key === 'placement_type') {
    selectedPlacementType.value = value
  } else if (key === 'work_set') {
    selectedWorkSet.value = value
  }
  refetchTableData()
}

function getDeleteKey(row) {
  return `${row.preset_kit_id}_${row.placement_type_id}_${row.work_set_id}_${row.material_set_id}`
}

async function deleteItem(row) {
  const deleteKey = getDeleteKey(row)
  deleting.value[deleteKey] = true

  const data = {
    preset_kit_id: props.presetKitId,
    placement_type_id: row.placement_type_id,
    work_set_id: row.work_set_id,
    material_set_id: row.material_set_id
  }
  const res = await deletePackageEntry(data)
  deleting.value[deleteKey] = false
  if (!res) return
  showSucceedSnackbar('Запись удалена успешно')
  refetchTableData()
}

function onAddToPackageClick() {
  showModal(MODAL_NAMES.AddPackageEntry, {
    title: 'Занести в пакет',
    params: {
      preset_kit_id: props.presetKitId
    },
    onSubmit: () => {
      refetchTableData()
    },
  })
}

function handleEditClick(row) {
  showModal(MODAL_NAMES.UpdPackageEntry, {
    onSubmit: (body) => updateSubmit(body),
    title: "Редактировать пакет",
    body: row,
  })
}

async function updateSubmit(body) {
  const res = await updPackageEntry(body);
  if (!res) return;
  showSucceedSnackbar();
  refetchTableData();
  closeModal();
}

watch(() => props.presetKitId, () => {
  selectedPlacementType.value = null
  selectedWorkSet.value = null
})
</script>

<style lang="scss" scoped>
.package-table {
  margin-top: 25px;
  .table-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 768px) {
    .row {
      margin-right: 16px;
    }
  }
  .createBtn {
    width: auto;
    font-weight: 400;
    height: 44px;
  }
}
</style>
